import React, { useState, useMemo } from 'react';
import { Plus, Trash2, Pencil, FileDown, Printer, ClipboardCheck, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { formatDate } from '../../lib/dateUtils';

/**
 * Tab: histórico de conteos físicos. Permite crear/editar/eliminar/exportar PDF.
 * Incluye búsqueda por texto (en fecha u observaciones) y filtros por rango de fechas.
 */
export const ConteosTab = ({
  conteos,
  loadingConteos,
  canVerSistemaConteo,
  onNuevoConteo,
  onEditConteo,
  onDeleteConteo,
  onExportPDF,
}) => {
  const [searchText, setSearchText] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');

  const filtered = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    return conteos.filter((c) => {
      const fechaIso = (c.fecha_conteo || '').slice(0, 10);
      if (fechaDesde && fechaIso < fechaDesde) return false;
      if (fechaHasta && fechaIso > fechaHasta) return false;
      if (term) {
        const haystack = [
          fechaIso,
          formatDate(c.fecha_conteo),
          c.observaciones || '',
        ].join(' ').toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [conteos, searchText, fechaDesde, fechaHasta]);

  const limpiar = () => {
    setSearchText('');
    setFechaDesde('');
    setFechaHasta('');
  };

  const hayFiltros = !!(searchText || fechaDesde || fechaHasta);

  return (
  <Card>
    <CardHeader className="flex flex-col gap-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <ClipboardCheck className="w-5 h-5" /> Conteo Físico de Inventario
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Al guardar, <strong>el inventario se ajusta</strong> al conteo físico y se registra un movimiento "Ajuste" por cada diferencia.
            También puedes activar el modo <strong>Cotejo Inicial</strong> en el modal para <strong>SUMAR</strong> al stock (carga de inventario inicial).
          </p>
        </div>
        <Button onClick={onNuevoConteo} data-testid="btn-nuevo-conteo">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Conteo
        </Button>
      </div>

      {/* Barra de búsqueda y filtros */}
      <div className="flex flex-wrap gap-2 items-end pt-1">
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
            Buscar por fecha u observaciones
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Ej. 27/4/2026, conteo semanal..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 h-9"
              data-testid="filtro-conteo-busqueda"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Desde</label>
          <Input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="w-[150px] h-9"
            data-testid="filtro-conteo-desde"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">Hasta</label>
          <Input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="w-[150px] h-9"
            data-testid="filtro-conteo-hasta"
            min={fechaDesde || undefined}
          />
        </div>
        {hayFiltros && (
          <Button variant="ghost" size="sm" onClick={limpiar} data-testid="btn-limpiar-filtros-conteo">
            Limpiar
          </Button>
        )}
      </div>
    </CardHeader>
    <CardContent>
      {loadingConteos ? (
        <div className="text-center py-8 text-muted-foreground">Cargando conteos...</div>
      ) : conteos.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <ClipboardCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No hay conteos registrados</p>
          <p className="text-xs mt-1">Crea tu primer conteo para empezar a llevar el control</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Sin resultados para los filtros aplicados</p>
          <Button variant="link" size="sm" onClick={limpiar} className="mt-2">Ver todos</Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="tabla-conteos">
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                {canVerSistemaConteo && <TableHead className="text-right">Total Sistema</TableHead>}
                <TableHead className="text-right">Total Físico</TableHead>
                {canVerSistemaConteo && <TableHead className="text-right">Diferencia</TableHead>}
                {canVerSistemaConteo && <TableHead className="text-center">Estado</TableHead>}
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const dif = c.total_diferencia || 0;
                const esCotejoInicial = !!(c.conteos?._meta?.cotejo_inicial);
                let estado, estadoVar, difCls;
                if (esCotejoInicial) {
                  estado = `Sumado (+${c.total_fisico || 0})`;
                  estadoVar = 'secondary';
                  difCls = 'text-green-600';
                } else {
                  estado = 'Cuadrado';
                  estadoVar = 'default';
                  if (dif < 0) { estado = `Faltante (${Math.abs(dif)})`; estadoVar = 'destructive'; }
                  else if (dif > 0) { estado = `Sobrante (+${dif})`; estadoVar = 'secondary'; }
                  difCls = dif < 0
                    ? 'text-destructive'
                    : (dif > 0 ? 'text-green-600' : 'text-muted-foreground');
                }
                return (
                  <TableRow key={c.id} className="hover:bg-accent/60 transition-colors">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {formatDate(c.fecha_conteo)}
                        {esCotejoInicial && (
                          <Badge
                            className="bg-green-600 hover:bg-green-600 text-white text-[10px] px-1.5 py-0"
                            title="Cotejo de inventario inicial (sumado al stock)"
                          >
                            INICIAL
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    {canVerSistemaConteo && (
                      <TableCell className="text-right tabular-nums">
                        {(c.total_sistema || 0).toLocaleString('en-US')}
                      </TableCell>
                    )}
                    <TableCell className="text-right tabular-nums">
                      {(c.total_fisico || 0).toLocaleString('en-US')}
                    </TableCell>
                    {canVerSistemaConteo && (
                      <TableCell className={`text-right tabular-nums font-semibold ${difCls}`}>
                        {dif > 0 ? `+${dif}` : dif}
                      </TableCell>
                    )}
                    {canVerSistemaConteo && (
                      <TableCell className="text-center">
                        <Badge variant={estadoVar} className="text-xs">{estado}</Badge>
                      </TableCell>
                    )}
                    <TableCell className="text-right whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onExportPDF(c, 'print')}
                        title="Imprimir"
                        data-testid={`btn-print-conteo-${c.id}`}
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onExportPDF(c, 'save')}
                        title="Descargar PDF"
                        data-testid={`btn-pdf-conteo-${c.id}`}
                      >
                        <FileDown className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditConteo(c)}
                        title="Ver / Editar"
                        data-testid={`btn-edit-conteo-${c.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDeleteConteo(c)}
                        title="Eliminar"
                        data-testid={`btn-delete-conteo-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
  );
};
